import { useEffect, useRef, useState } from "react";
import { ShinyButton } from "@/components/ui/ShinyButton";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingCart, User, LogOut, Package, Heart, LayoutDashboard, Landmark, FileText, Building2, RotateCcw, LifeBuoy, ChevronDown, Search, Tags, TicketPercent, Crown, Store } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { useFestivalTheme } from "@/context/FestivalThemeContext";
import { api } from "@/lib/api";

const ACCOUNT_ITEMS = [
  { icon: Package, label: "Orders", path: "/orders", testid: "account-orders" },
  { icon: RotateCcw, label: "Buy Again", path: "/orders#buy-again", testid: "account-buy-again" },
  { icon: Heart, label: "Wishlist", path: "/wishlist", testid: "account-wishlist" },
  { icon: Building2, label: "Business Profile", path: "/account/business", testid: "account-business" },
  { icon: Landmark, label: "PAQVERO Credit", path: "/account/credit", testid: "account-credit" },
  { icon: Crown, label: "PAQVERO PRIME", path: "/account/prime", testid: "account-prime" },
  { icon: FileText, label: "Invoices", path: "/account/invoices", testid: "account-invoices" },
  { icon: LifeBuoy, label: "Support", action: "chat", testid: "account-support" },
];

const DEAL_ITEMS = [
  { label: "Bulk Deals", desc: "MOQ pricing for volume buyers", href: "/shop?bulk=1", testid: "nav-link-bulk-deals-all" },
  { label: "Offers & Discounts", desc: "Everything currently on sale", href: "/shop?offers=1", testid: "nav-link-offers" },
];

function DealsDropdown({ navigate, active }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="nav-link-bulk-deals"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`group relative flex items-center gap-1 text-sm font-medium transition-colors duration-300 hoverable-t1 ${open || active ? "text-amber-500" : "text-2"}`}
      >
        Bulk Deals
        <ChevronDown className={`h-3.5 w-3.5 text-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        <span className={`absolute -bottom-1.5 left-0 h-0.5 rounded-full bg-amber-500 transition-all duration-300 ${active ? "w-full" : "w-0 group-hover:w-full"}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.16 }}
            data-testid="nav-link-bulk-deals-menu"
            className="absolute left-1/2 top-9 z-50 w-64 -translate-x-1/2 rounded-xl border border-line bg-surface p-2 shadow-lift"
          >
            {DEAL_ITEMS.map((it) => (
              <button
                key={it.testid}
                data-testid={it.testid}
                onClick={() => { setOpen(false); navigate(it.href); }}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hoverable"
              >
                <TicketPercent className="h-4 w-4 shrink-0 text-amber-500" />
                <span>
                  <span className="block text-sm font-semibold text-1">{it.label}</span>
                  <span className="block text-[11px] text-3">{it.desc}</span>
                </span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export const scrollToSection = (href) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset: -72, duration: 1.5 });
  } else {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  }
};

function PrimeDropdown({ navigate, activePrime, activeStore }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => { document.removeEventListener("mousedown", close); clearTimeout(closeTimer.current); };
  }, []);

  const openMenu = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const scheduleClose = () => { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setOpen(false), 180); };
  const toggle = () => (open ? setOpen(false) : openMenu());
  const active = activePrime || activeStore;

  const items = [
    { label: "PAQVERO PRIME", desc: "Membership & delivery benefits", icon: Crown, path: "/prime", testid: "nav-link-prime-membership", on: activePrime },
    { label: "Prime Store", desc: "Member-priced products", icon: Store, path: "/prime-store", testid: "nav-link-prime-store", on: activeStore },
  ];

  return (
    <div className="relative" ref={ref} onMouseEnter={openMenu} onMouseLeave={scheduleClose}>
      <button
        data-testid="nav-link-prime"
        onClick={toggle}
        aria-expanded={open}
        className={`group relative flex items-center gap-1 text-sm font-bold transition-colors duration-300 hoverable-t1 ${open || active ? "text-amber-500" : "text-2"}`}
      >
        <Crown className="h-3.5 w-3.5 text-amber-500" />
        Prime
        <ChevronDown className={`h-3.5 w-3.5 text-3 transition-transform duration-300 ${open ? "rotate-180 text-amber-600" : ""}`} />
        <span className={`absolute -bottom-1.5 left-0 h-0.5 rounded-full bg-amber-500 transition-all duration-300 ${active ? "w-full" : "w-0 group-hover:w-full"}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98, transition: { duration: 0.14 } }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            style={{ transformOrigin: "top center" }}
            data-testid="nav-link-prime-menu"
            className="absolute left-1/2 top-9 z-50 w-60 -translate-x-1/2 rounded-2xl border border-line bg-surface p-2 shadow-lift"
          >
            {items.map((it, i) => (
              <motion.button
                key={it.testid}
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.18, delay: Math.min(i * 0.03, 0.24), ease: "easeOut" }}
                data-testid={it.testid}
                onClick={() => { setOpen(false); navigate(it.path); }}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-200 hover:bg-hover ${it.on ? "bg-hover" : ""}`}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-500/10">
                  <it.icon className="h-4 w-4 text-amber-500" />
                </span>
                <span>
                  <span className={`block text-sm font-bold ${it.on ? "text-amber-600" : "text-1"}`}>{it.label}</span>
                  <span className="block text-[11px] text-3">{it.desc}</span>
                </span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function NavDropdown({ label, testid, fetcher, itemHref, icon: Icon, navigate }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const ref = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => { document.removeEventListener("mousedown", close); clearTimeout(closeTimer.current); };
  }, []);

  const load = async () => {
    if (items === null) {
      try { setItems(await fetcher()); } catch { setItems([]); }
    }
  };
  const openMenu = () => { clearTimeout(closeTimer.current); setOpen(true); load(); };
  const scheduleClose = () => { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setOpen(false), 180); };
  const toggle = () => (open ? setOpen(false) : openMenu());

  return (
    <div className="relative" ref={ref} onMouseEnter={openMenu} onMouseLeave={scheduleClose}>
      <button
        data-testid={testid}
        onClick={toggle}
        aria-expanded={open}
        className={`group flex items-center gap-1 text-sm font-medium transition-colors duration-300 hoverable-t1 ${open ? "text-amber-600" : "text-2"}`}
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 text-3 transition-transform duration-300 ${open ? "rotate-180 text-amber-600" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98, transition: { duration: 0.14 } }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            style={{ transformOrigin: "top center" }}
            data-testid={`${testid}-menu`}
            data-lenis-prevent
            className="absolute left-1/2 top-9 z-50 max-h-[60vh] w-64 -translate-x-1/2 overflow-y-auto rounded-2xl border border-line bg-surface p-2 shadow-lift"
          >
            {items === null ? (
              <p className="px-3 py-4 text-center text-xs text-3">Loading…</p>
            ) : items.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-3">Nothing here yet</p>
            ) : (
              items.map((it, i) => (
                <motion.button
                  key={it.id}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.18, delay: Math.min(i * 0.03, 0.24), ease: "easeOut" }}
                  data-testid={`${testid}-item-${(it.slug || it.name).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => { setOpen(false); navigate(itemHref(it)); }}
                  className="group/item flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left text-sm text-2 transition-colors duration-200 hover:bg-[#C88732]/[0.08] hover:text-[#A96C1E]"
                >
                  <span className="flex items-center gap-2 truncate">
                    <Icon className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                    <span className="truncate">{it.name}</span>
                  </span>
                  <span className="shrink-0 text-[11px] text-3">{it.count}</span>
                </motion.button>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { cartCount, setDrawerOpen, wishlistIds } = useStore();
  const { content: themeContent, isFestival } = useFestivalTheme();
  const logoDark = themeContent.logo_dark || "/paqvero-logo.png";
  const logoLight = themeContent.logo_light || "/paqvero-logo-light.png";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: href } });
    } else {
      scrollToSection(href);
    }
  };

  const handleLogout = () => {
    setAccountOpen(false);
    setOpen(false);
    logout();
    navigate("/");
  };

  const accountNav = (item) => {
    setAccountOpen(false);
    setOpen(false);
    if (item.action === "chat") {
      window.dispatchEvent(new Event("paqvero:open-chat"));
      return;
    }
    navigate(item.path);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    try {
      const recent = JSON.parse(localStorage.getItem("paqvero-recent-searches") || "[]");
      localStorage.setItem("paqvero-recent-searches", JSON.stringify([q, ...recent.filter((r) => r !== q)].slice(0, 6)));
    } catch { /* private mode */ }
    setQuery("");
    setOpen(false);
    navigate(`/shop?q=${encodeURIComponent(q)}`);
  };

  const isShop = location.pathname.startsWith("/shop");
  const solid = scrolled || location.pathname !== "/";

  const plainLink = (label, href, testid, active) => (
    <button
      key={testid}
      data-testid={testid}
      onClick={() => go(href)}
      className={`group relative text-sm font-medium transition-colors duration-300 hoverable-t1 ${active ? "text-amber-500" : "text-2"}`}
    >
      {label}
      <span className={`absolute -bottom-1.5 left-0 h-0.5 rounded-full bg-amber-500 transition-all duration-300 ${active ? "w-full" : "w-0 group-hover:w-full"}`} />
    </button>
  );

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        solid ? "border-b border-line bg-nav shadow-card backdrop-blur-xl" : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <button data-testid="navbar-logo" onClick={() => go("/")} className="group flex shrink-0 items-center gap-2.5">
          <img
            src={logoDark}
            alt="PAQVERO"
            width="133"
            height="32"
            className="brand-logo brand-logo--dark h-8 w-auto transition-transform duration-300 group-hover:scale-[1.03]"
          />
          <img
            src={logoLight}
            alt=""
            aria-hidden="true"
            width="133"
            height="32"
            className="brand-logo brand-logo--light h-8 w-auto transition-transform duration-300 group-hover:scale-[1.03]"
          />
        </button>

        <nav className="hidden items-center gap-5 lg:flex xl:gap-7" data-testid="navbar-links">
          {plainLink("Products", "/shop", "nav-link-shop", isShop && !location.search)}
          <NavDropdown label="Categories" testid="nav-link-categories" icon={Tags} navigate={navigate}
            fetcher={async () => { const d = (await api.get("/shop/categories")).data; return d.categories || d || []; }}
            itemHref={(c) => `/shop?category=${encodeURIComponent(c.name)}`} />
          <DealsDropdown navigate={navigate} active={location.search.includes("bulk=1") || location.search.includes("offers=1")} />
          <PrimeDropdown navigate={navigate} activePrime={location.pathname === "/prime"} activeStore={location.pathname === "/prime-store"} />
        </nav>

        <form onSubmit={submitSearch} className="hidden min-w-0 max-w-[200px] flex-1 items-center lg:flex xl:max-w-xs" role="search">
          <div className="relative w-full">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-3" />
            <input
              data-testid="navbar-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products, brands…"
              className="input-pq h-10 w-full rounded-lg pl-9 pr-3 text-sm"
            />
          </div>
        </form>

        <div className="flex shrink-0 items-center gap-2">

          <button
            data-testid="navbar-wishlist-btn"
            onClick={() => navigate("/wishlist")}
            className="relative hidden h-10 w-10 items-center justify-center rounded-full bg-[#20221F] text-[#F8F6F0] transition-colors hover:bg-[#C88732] hover:text-[#FFFEFA] sm:flex"
            aria-label="Wishlist"
          >
            <Heart className="h-[18px] w-[18px]" />
            {wishlistIds?.length > 0 && (
              <span data-testid="navbar-wishlist-count" className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-black">
                {wishlistIds.length}
              </span>
            )}
          </button>

          <button
            data-testid="navbar-cart-btn"
            onClick={() => setDrawerOpen(true)}
            className="relative flex h-10 w-10 items-center justify-center rounded-full bg-[#20221F] text-[#F8F6F0] transition-colors hover:bg-[#C88732] hover:text-[#FFFEFA]"
            aria-label="Open cart"
          >
            <ShoppingCart className="h-[18px] w-[18px]" />
            {cartCount > 0 && (
              <span
                data-testid="navbar-cart-count"
                className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-black"
              >
                {cartCount}
              </span>
            )}
          </button>

          {user ? (
            <div className="relative">
              <button
                data-testid="navbar-account-btn"
                onClick={() => setAccountOpen((v) => !v)}
                className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-amber-600/50 bg-amber-500 text-sm font-bold text-[#1a1000]"
                aria-label="Account"
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name || "Account"} referrerPolicy="no-referrer" className="h-full w-full object-cover" data-testid="navbar-account-avatar" />
                ) : (
                  user.name?.[0]?.toUpperCase() || <User className="h-4 w-4" />
                )}
              </button>
              <AnimatePresence>
                {accountOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.18 }}
                    className="absolute right-0 top-12 w-52 overflow-hidden rounded-xl border border-line bg-surface shadow-lift"
                    data-testid="navbar-account-menu"
                    data-lenis-prevent
                  >
                    <div className="flex items-center gap-3 border-b border-line px-4 py-3">
                      {user.picture ? (
                        <img src={user.picture} alt="" referrerPolicy="no-referrer" className="h-9 w-9 shrink-0 rounded-full object-cover" />
                      ) : (
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500 text-sm font-bold text-[#1a1000]">{user.name?.[0]?.toUpperCase()}</span>
                      )}
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-1">{user.name}</p>
                        <p className="truncate text-xs text-3">{user.email}</p>
                      </div>
                    </div>
                    {[
                      ...ACCOUNT_ITEMS,
                      ...(user.role === "admin" || user.role === "owner"
                        ? [{ icon: LayoutDashboard, label: "Admin Dashboard", path: "/admin", testid: "account-admin" }]
                        : []),
                    ].map((item) => (
                      <button
                        key={item.testid}
                        data-testid={item.testid}
                        onClick={() => accountNav(item)}
                        className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-sm text-2 transition-colors hoverable hoverable-t1"
                      >
                        <item.icon className="h-4 w-4 text-amber-500" />
                        {item.label}
                      </button>
                    ))}
                    <button
                      data-testid="account-logout"
                      onClick={handleLogout}
                      className="flex w-full items-center gap-2.5 border-t border-line px-4 py-3 text-left text-sm text-2 transition-colors hoverable hover:text-red-400"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <div className="hidden sm:block">
              <ShinyButton
                data-testid="navbar-signin-btn"
                onClick={() => navigate("/login")}
                contentClassName="px-6 py-2.5 text-sm font-bold"
              >
                Sign In
              </ShinyButton>
            </div>
          )}

          <button
            data-testid="navbar-mobile-menu-btn"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-line text-1 lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="navbar-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="max-h-[calc(100dvh-72px)] overflow-y-auto border-b border-line bg-surface backdrop-blur-xl lg:hidden"
            data-lenis-prevent
          >
            <div className="space-y-1 px-4 py-5 sm:px-6">
              <form onSubmit={submitSearch} className="pb-3" role="search">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-3" />
                  <input
                    data-testid="mobile-search-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search products, brands…"
                    className="input-pq h-11 w-full rounded-lg pl-9 pr-3 text-sm"
                  />
                </div>
              </form>
              {[
                { label: "Products", href: "/shop", testid: "mobile-nav-link-shop" },
                { label: "Bulk Deals", href: "/shop?bulk=1", testid: "mobile-nav-link-bulk-deals" },
                { label: "Offers", href: "/shop?offers=1", testid: "mobile-nav-link-offers" },
                { label: "Track Your Order", href: "/track", testid: "mobile-nav-link-tracking" },
                { label: "About PAQVERO", href: "#about", testid: "mobile-nav-link-about" },
              ].map((link) => (
                <button
                  key={link.testid}
                  data-testid={link.testid}
                  onClick={() => go(link.href)}
                  className="block w-full rounded-lg px-3 py-3 text-left font-display text-lg text-2 transition-colors hoverable hoverable-t1"
                >
                  {link.label}
                </button>
              ))}
              {!user && (
                <button
                  data-testid="mobile-signin-btn"
                  onClick={() => { setOpen(false); navigate("/login"); }}
                  className="block w-full rounded-lg px-3 py-3 text-left font-display text-lg text-2 hoverable hoverable-t1"
                >
                  Sign In
                </button>
              )}
              {user && (
                <div className="mt-4 border-t border-line pt-4" data-testid="mobile-account-section">
                  <p className="truncate px-3 pb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-3">
                    {user.name} · {user.email}
                  </p>
                  {[
                    ...ACCOUNT_ITEMS,
                    ...(user.role === "admin" || user.role === "owner"
                      ? [{ icon: LayoutDashboard, label: "Admin Dashboard", path: "/admin", testid: "account-admin" }]
                      : []),
                  ].map((item) => (
                    <button
                      key={item.testid}
                      data-testid={`mobile-${item.testid}`}
                      onClick={() => accountNav(item)}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left font-display text-lg text-2 transition-colors hoverable hoverable-t1"
                    >
                      <item.icon className="h-[18px] w-[18px] text-amber-500" />
                      {item.label}
                    </button>
                  ))}
                  <button
                    data-testid="mobile-account-logout"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left font-display text-lg text-2 transition-colors hoverable hover:text-red-400"
                  >
                    <LogOut className="h-[18px] w-[18px]" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
